import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { useCRMData } from '../context/CRMDataContext';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { formatCurrency } from '../utils/helpers';

const SellerDashboard = () => {
  const { deals, activities, budgets } = useCRMData();
  const { currentUser } = useAuth();

  const COMMISSION_RATE = 0.10;

  const myDeals = deals.filter(d => d.owner_user_id === currentUser.user_id);
  const myWonDeals = myDeals.filter(d => d.status === 'Vunnet');
  const myOpenDeals = myDeals.filter(d => d.status === 'Åpen');

  const totalWonValue = myWonDeals.reduce((sum, d) => sum + Number(d.value), 0);
  const estimatedCommission = totalWonValue * COMMISSION_RATE;
  const pipelineValue = myOpenDeals.reduce((sum, d) => sum + Number(d.value), 0);
  const weightedPipeline = myOpenDeals.reduce((sum, d) => sum + (Number(d.value) * (d.probability / 100)), 0);

  // Finn aktivt budsjett for brukeren i dag
  const today = new Date().toISOString().split('T')[0];
  const activeBudgets = budgets.filter(b => 
    b.type === 'User' && 
    b.target_id === currentUser.user_id && 
    b.start_date <= today && 
    b.end_date >= today
  );

  // Beregn hovedbudsjett (Omsetning) basert på ny datastruktur eller fallback til gammel
  const currentBudgetAmount = activeBudgets.reduce((sum, b) => {
    if (b.goals && b.goals.length > 0) {
      const currencyGoals = b.goals.filter(g => g.metric === 'currency');
      return sum + currencyGoals.reduce((gSum, g) => gSum + Number(g.value), 0);
    }
    return sum + Number(b.amount || 0);
  }, 0);

  const budgetProgress = currentBudgetAmount > 0 ? (totalWonValue / currentBudgetAmount) * 100 : 0;
  const isAhead = totalWonValue >= currentBudgetAmount;
  const gapAmount = Math.abs(currentBudgetAmount - totalWonValue);

  const getSmartTasks = () => {
    const tasks = [];
    const overdueActivities = activities.filter(a => a.user_id === currentUser.user_id && a.status !== 'Gjennomført' && a.date < today);
    
    if (overdueActivities.length > 0) {
      tasks.push({ type: 'urgent', text: `Du har ${overdueActivities.length} forfalte aktiviteter. Få dem unna!`, icon: "⚠️", color: 'text-red-600 bg-red-50' });
    }

    const closingSoon = myOpenDeals.filter(d => {
      if (!d.expected_close_date) return false;
      const closeDate = new Date(d.expected_close_date);
      const now = new Date();
      const diffTime = closeDate - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays >= 0 && diffDays <= 7;
    });

    if (closingSoon.length > 0) {
      tasks.push({ type: 'focus', text: `${closingSoon.length} deals forventes signert denne uken. Følg opp!`, icon: "💰", color: 'text-green-600 bg-green-50' });
    }

    if (tasks.length === 0) {
      tasks.push({ type: 'info', text: 'Ingen kritiske hendelser. Hva med å ringe 5 nye prospects?', icon: "⚡", color: 'text-blue-600 bg-blue-50' });
    }

    return tasks;
  };

  const smartTasks = getSmartTasks();

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Min Salgsoversikt</h1>
          <p className="text-slate-500 font-medium mt-1">Velkommen tilbake, {currentUser.name}</p>
        </div>
      </div>

      {/* Budget Tracker Banner */}
      {currentBudgetAmount > 0 && (
        <div className="bg-white p-8 rounded-[2rem] border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="flex justify-between items-end mb-6 relative z-10">
            <div>
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-2">
                <SafeIcon icon="🎯" /> Hovedmål for perioden (Omsetning)
              </p>
              <h2 className="text-3xl font-black text-slate-900">{formatCurrency(currentBudgetAmount)}</h2>
            </div>
            <div className="text-right">
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Ligger an til</p>
              <h2 className={`text-2xl font-black ${isAhead ? 'text-emerald-600' : 'text-rose-600'}`}>
                {budgetProgress.toFixed(1)}%
              </h2>
            </div>
          </div>
          
          <div className="relative z-10 w-full bg-slate-100 rounded-full h-4 mb-4 overflow-hidden shadow-inner">
            <motion.div 
              initial={{ width: 0 }}
              animate={{ width: `${Math.min(budgetProgress, 100)}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className={`h-full rounded-full ${isAhead ? 'bg-emerald-500' : 'bg-blue-600'}`}
            />
          </div>

          <div className="relative z-10 flex justify-between items-center">
            <p className="text-sm font-bold text-slate-700">Faktisk: {formatCurrency(totalWonValue)}</p>
            <p className={`text-xs font-bold px-3 py-1 rounded-lg ${isAhead ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
              {isAhead 
                ? `🚀 Du ligger ${formatCurrency(gapAmount)} FORAN budsjett!` 
                : `⚠️ Du ligger ${formatCurrency(gapAmount)} BAK budsjett.`}
            </p>
          </div>
        </div>
      )}

      {/* Andre KPI-mål fra budsjettet (Ny seksjon) */}
      {activeBudgets.length > 0 && activeBudgets.some(b => b.goals && b.goals.filter(g => g.metric !== 'currency').length > 0) && (
        <div className="bg-slate-50 border border-slate-200 p-6 rounded-[2rem]">
          <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4">Andre KPI-Mål</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {activeBudgets.flatMap(b => (b.goals || []).map(g => ({ ...g, budgetStartDate: b.start_date }))).filter(g => g.metric !== 'currency').map((goal, idx) => {
              // Veldig enkel mock for å vise fremdrift på andre KPIer
              // I et ekte system ville vi kalkulert f.eks antall møter booket
              let currentProgress = 0;
              if(goal.metric === 'meetings_count') currentProgress = activities.filter(a => a.user_id === currentUser.user_id && a.type.toLowerCase().includes('møte') && a.date >= goal.budgetStartDate).length;
              if(goal.metric === 'deals_count') currentProgress = myWonDeals.length;

              return (
                <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-slate-100">
                  <p className="text-[10px] font-bold text-slate-400 uppercase truncate mb-1">
                    {goal.metric === 'meetings_count' ? 'Møter' : goal.metric === 'deals_count' ? 'Salg (stk)' : 'KPI Mål'}
                  </p>
                  <p className="text-xl font-black text-slate-900">{currentProgress} <span className="text-sm text-slate-400 font-medium">/ {goal.value}</span></p>
                  <div className="w-full bg-slate-100 h-1.5 rounded-full mt-2 overflow-hidden">
                    <div className="bg-blue-500 h-full" style={{ width: `${Math.min((currentProgress/goal.value)*100, 100)}%` }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs font-black uppercase tracking-widest">Estimert Provisjon</h3>
            <div className="p-2 bg-emerald-50 rounded-xl text-emerald-600">
              <SafeIcon icon="💰" className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-slate-900">{formatCurrency(estimatedCommission)}</p>
          <p className="text-[10px] font-bold text-emerald-600 flex items-center mt-2 uppercase tracking-widest">
            <SafeIcon icon="📈" className="mr-1" />
            Basert på vunnet ({COMMISSION_RATE*100}%)
          </p>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs font-black uppercase tracking-widest">Mine Vunnet (YTD)</h3>
            <div className="p-2 bg-blue-50 rounded-xl text-blue-600">
              <SafeIcon icon="✅" className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-slate-900">{formatCurrency(totalWonValue)}</p>
          <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest">{myWonDeals.length} avtaler signert</p>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs font-black uppercase tracking-widest">Min Pipeline</h3>
            <div className="p-2 bg-purple-50 rounded-xl text-purple-600">
              <SafeIcon icon="⚡" className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-slate-900">{formatCurrency(pipelineValue)}</p>
          <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest">Vektet: {formatCurrency(weightedPipeline)}</p>
        </motion.div>

        <motion.div whileHover={{ y: -2 }} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-slate-400 text-xs font-black uppercase tracking-widest">Aktiviteter</h3>
            <div className="p-2 bg-orange-50 rounded-xl text-orange-600">
              <SafeIcon icon="🕒" className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-slate-900">
            {activities.filter(a => a.user_id === currentUser.user_id && a.status === 'Gjennomført').length}
          </p>
          <p className="text-[10px] font-bold text-slate-500 mt-2 uppercase tracking-widest">Uført hittil</p>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
              <h3 className="font-black text-slate-900 flex items-center gap-2">
                <SafeIcon icon="🎯" className="text-blue-600" />
                Dagens Fokus
              </h3>
            </div>
            <div className="p-6 space-y-3">
              {smartTasks.map((task, index) => (
                <div key={index} className={`flex items-start p-4 rounded-xl border border-transparent ${task.color} shadow-sm`}>
                  <SafeIcon icon={task.icon} className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-sm font-bold bg-transparent">{task.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6">
          <h3 className="font-black text-slate-900 mb-6">Siste Hendelser</h3>
          <div className="space-y-6">
            {activities.slice(0, 5).map(activity => (
              <div key={activity.activity_id} className="flex items-start gap-4">
                <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500 shrink-0 shadow-sm shadow-blue-200"></div>
                <div>
                  <p className="text-sm font-bold text-slate-800 leading-tight">{activity.title}</p>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">
                    {new Date(activity.date).toLocaleDateString()}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default SellerDashboard;